import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Login from "@/pages/Login";
import NotFound from "./pages/NotFound";
import { ProtectedRoute } from "@/config/ProtectedRoute";
// The app's ONE QueryClient. A module singleton rather than a `useMemo` here so
// `AuthContext.logout()` — which is also called from non-component code in
// `httpService` — can wipe the cache on the way out.
import { queryClient } from "@/api/queryClient";
import ToasterProvider from "@/components/ToasterProvider";
import SessionManager from "@/components/SessionManager";
import { useAuth } from "@/contexts/AuthContext";
// Migrated New UI ("insight") — a shared layout with three dedicated routes.
import DaylightLayout from "@/components/daylight-insight/DaylightLayout";
import { NEW_UI_HOME } from "@/components/daylight-insight/DaylightUiToggle";
import GuideViewInsightPage from "@/pages/guide-view-insight";
import DashboardInsightPage from "@/pages/dashboard-insight";
import AllMetricsInsightPage from "@/pages/all-metrics-insight";
// Charlie (Administrator Daily Brief) — account-gated, mock data for now.
import CharlieInsightPage from "@/pages/charlie-insight";

const App = () => {
  const { token, isTokenExpired, logout, user } = useAuth();
  console.log("version", "1.0.10");

  const AuthCheck = ({
    element: Component,
    isAuthRoute = false,
  }: {
    element: React.ComponentType;
    isAuthRoute?: boolean;
  }) => {
    const idToken = token;
    if (isAuthRoute) {
      return idToken ? <Navigate to={NEW_UI_HOME} replace /> : <Component />;
    }

    return idToken ? <Component /> : <Navigate to="/login" replace />;
  };

  const RoleRoute = ({
    element: Component,
    allowedRoles,
  }: {
    element: React.ComponentType;
    allowedRoles: string[];
  }) => {
    if (!token || isTokenExpired(token)) {
      logout();
      return <Navigate to="/login" replace />;
    }

    if (!allowedRoles.some((item) => user?.roles.includes(item))) {
      logout();
      return <Navigate to="/login" replace />;
    }

    return <Component />;
  };

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ToasterProvider />
        <SessionManager />
        <BrowserRouter>
          <Routes>
            <Route
              path="/login"
              element={<AuthCheck isAuthRoute element={Login} />}
            />

            <Route path="/" element={<Navigate to={NEW_UI_HOME} replace />} />

            {/* Retired Old UI routes — kept as redirects so existing bookmarks
                and cached service-worker entries don't 404. */}
            <Route
              path="/daylight"
              element={<Navigate to={NEW_UI_HOME} replace />}
            />
            <Route
              path="/daylight/all-metrics"
              element={<Navigate to="/all-metrics-insight" replace />}
            />

            {/* Migrated New UI — shared layout (auth + role gated) with three
                dedicated routes that replace the original in-page tab nav. */}
            <Route
              element={
                <ProtectedRoute
                  element={
                    <RoleRoute
                      element={DaylightLayout}
                      allowedRoles={["bluebird-app-daylight"]}
                    />
                  }
                />
              }
            >
              <Route
                path="/dashboard-insight"
                element={<DashboardInsightPage />}
              />
              <Route
                path="/all-metrics-insight"
                element={<AllMetricsInsightPage />}
              />
              <Route
                path="/guide-view-insight"
                element={<GuideViewInsightPage />}
              />
              <Route path="/charlie-insight" element={<CharlieInsightPage />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
